import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gem, Lock, Crown, Loader2, ExternalLink, MessageSquare, LogIn, ArrowLeft } from "lucide-react";
import { API } from "@/assets";
import { useSiteAuth, signInWithGoogle } from "@/components/GoogleAuth";

const VAULT_CSS = `
@keyframes vaultFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-8px) } }
@keyframes vaultSweep { from { transform: translateX(-110%) skewX(-12deg) } to { transform: translateX(220%) skewX(-12deg) } }
@keyframes crystalIdle { 0%,100% { filter: drop-shadow(0 0 4px currentColor) } 50% { filter: drop-shadow(0 0 12px currentColor) } }
@keyframes goldReveal { from { opacity: 0; transform: translateY(14px) scale(.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
`;

const CRYSTALS = [
  { id: "amethyst", label: "Amethyst", color: "#A855F7", freq: "963 Hz · crown frequency" },
  { id: "obsidian", label: "Obsidian", color: "#71717A", freq: "396 Hz · liberation tone" },
  { id: "emerald", label: "Emerald", color: "#00FF66", freq: "528 Hz · heart resonance" },
];
const SEQUENCE = ["amethyst", "obsidian", "emerald"];

export default function VipVaultPage() {
  const { user } = useSiteAuth();
  const [picks, setPicks] = useState([]);
  const [state, setState] = useState("idle"); // idle | wrong | unlocked
  const [status, setStatus] = useState(null);
  const [vault, setVault] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    fetch(`${API}/medusa/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then(setStatus).catch(() => setStatus({ member: false }));
  }, [user]);

  useEffect(() => {
    if (state !== "unlocked" || !status?.member) return;
    fetch(`${API}/vault`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then((d) => setVault(d?.items || [])).catch(() => setVault([]));
  }, [state, status]);

  const pick = (id) => {
    if (state === "unlocked") return;
    const next = [...picks, id];
    setPicks(next);
    if (!next.every((v, i) => v === SEQUENCE[i])) {
      setState("wrong");
      setTimeout(() => { setPicks([]); setState("idle"); }, 900);
      return;
    }
    if (next.length === SEQUENCE.length) setState("unlocked");
  };

  const subscribe = async (plan) => {
    if (!user) { signInWithGoogle(); return; }
    setBusy(plan);
    try {
      const res = await fetch(`${API}/medusa/subscribe`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, origin_url: window.location.origin }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Checkout failed to start.");
      window.location.href = data.checkout_url;
    } catch { setBusy(""); }
  };

  return (
    <main className="min-h-screen bg-[#07070B] text-ice relative overflow-hidden" data-testid="vip-vault-page">
      <style>{VAULT_CSS}</style>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/3 w-[480px] h-[480px] rounded-full bg-[#FFB800]/8 blur-[130px]" />
        <div className="absolute bottom-0 -left-24 w-[420px] h-[420px] rounded-full bg-teal-500/10 blur-[120px]" />
        <div className="absolute top-1/2 -right-24 w-[380px] h-[380px] rounded-full bg-purple-600/10 blur-[120px]" />
      </div>

      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 pt-24 sm:pt-28 pb-20">
        <Link to="/" className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-ice/45 hover:text-teal-300 transition-colors" data-testid="vault-back-link">
          <ArrowLeft size={13} /> Back to the Label
        </Link>

        <div className="mt-8 text-center">
          <div className="relative inline-block">
            <div className="absolute -inset-8 bg-[radial-gradient(circle,rgba(255,184,0,0.18),transparent_70%)] blur-2xl" />
            <img src="/medusa-logo.webp" alt="Cold-Blooded Records Medusa" className="relative w-40 sm:w-52 mx-auto" style={{ animation: "vaultFloat 5s ease-in-out infinite", filter: "drop-shadow(0 0 26px rgba(255,184,0,0.4)) drop-shadow(0 0 70px rgba(168,85,247,0.3))" }} data-testid="vault-logo" />
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-[#FFB800]">High Exclusivity Platform</p>
          <h1 className="mt-2 font-display text-3xl sm:text-5xl font-bold text-white" style={{ textShadow: "0 0 18px rgba(255,184,0,0.6), 0 0 46px rgba(168,85,247,0.4)" }} data-testid="vault-title">
            THE VIP VAULT
          </h1>
          <p className="mt-3 text-sm sm:text-base text-ice/55 max-w-lg mx-auto">Unreleased multi-track stems. Producer project files. High-tier sample packs. Early-access downloads. No passwords — only resonance.</p>
        </div>

        {state !== "unlocked" ? (
          <div className="mt-10 bg-[#0B0B10]/90 border border-teal-500/25 rounded-3xl p-6 sm:p-10 backdrop-blur" data-testid="vault-gate">
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-teal-300 mb-2">Energy-Aligned Entry</p>
            <p className="text-center text-sm text-ice/60 max-w-xl mx-auto leading-relaxed" data-testid="vault-hint">
              Three frequencies guard the vault. Align them in the order of the old rite:
              <span className="text-purple-300"> the crown (963)</span> opens the way,
              <span className="text-zinc-300"> the liberator (396)</span> follows,
              <span className="text-emerald-300"> the heart (528)</span> seals it.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-5 max-w-xl mx-auto" data-testid="vault-crystal-grid">
              {CRYSTALS.map(({ id, label, color, freq }) => {
                const at = picks.indexOf(id);
                return (
                  <button key={id} onClick={() => pick(id)} className={`rounded-2xl border p-4 sm:p-6 text-center transition-all duration-300 active:scale-95 ${state === "wrong" ? "border-red-500/60" : ""}`}
                    style={{
                      borderColor: at >= 0 ? color : "#26262e",
                      boxShadow: at >= 0 ? `0 0 26px ${color}55` : "none",
                      animation: "crystalIdle 2.4s ease-in-out infinite",
                      color,
                    }}
                    data-testid={`vault-crystal-${id}`}>
                    <Gem size={34} className="mx-auto" style={{ color }} />
                    <p className="mt-2.5 font-mono text-[10px] uppercase tracking-[0.18em]">{label}</p>
                    <p className="text-[9px] font-mono text-ice/35 mt-1">{freq}</p>
                    <p className="text-[9px] font-mono mt-1" style={{ color: at >= 0 ? color : "#3f3f46" }}>{at >= 0 ? `ALIGNED ${at + 1}` : "DORMANT"}</p>
                  </button>
                );
              })}
            </div>
            {state === "wrong" && <p className="mt-4 text-center text-xs text-red-400 font-mono" data-testid="vault-wrong">Frequencies clash. The grid resets…</p>}
          </div>
        ) : (
          <div className="mt-10 relative overflow-hidden bg-[#0B0B10]/90 border border-[#FFB800]/50 rounded-3xl p-6 sm:p-10 backdrop-blur" style={{ animation: "goldReveal .7s ease" }} data-testid="vault-interior">
            <div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-yellow-200/20 to-transparent pointer-events-none" style={{ animation: "vaultSweep 1.6s ease forwards" }} />
            <div className="relative">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB800] mb-1">Resonance Confirmed</p>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-white">The Vault Is Open</h2>

              {status?.member ? (
                vault === null ? (
                  <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#FFB800]" size={22} /></div>
                ) : vault.length === 0 ? (
                  <p className="mt-4 text-sm text-ice/55" data-testid="vault-empty">Fresh drops are being mastered — check back after the next Petrified Countdown.</p>
                ) : (
                  <div className="mt-6 space-y-3" data-testid="vault-items">
                    {vault.map((it) => (
                      <div key={it.v_id} className="flex items-center gap-4 bg-black/40 border border-[#FFB800]/25 rounded-xl px-5 py-4" data-testid={`vault-item-${it.v_id}`}>
                        {it.kind === "link" ? <ExternalLink size={15} className="text-[#FFB800] shrink-0" /> : <MessageSquare size={15} className="text-[#FFB800] shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-white truncate">{it.title}</p>
                          {it.kind === "note" && it.body && <p className="text-xs text-ice/55 mt-1 whitespace-pre-wrap">{it.body}</p>}
                        </div>
                        {it.kind === "link" && it.url && (
                          <a href={it.url} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.12em] border border-[#FFB800]/50 text-[#FFB800] rounded-full px-4 py-2 hover:bg-[#FFB800] hover:text-black transition-all" data-testid={`vault-open-${it.v_id}`}>Open</a>
                        )}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="mt-5 bg-black/40 border border-[#FFB800]/25 rounded-2xl p-6" data-testid="vault-member-teaser">
                  <div className="flex items-start gap-3">
                    <Lock size={18} className="text-[#FFB800] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-ice/70 leading-relaxed">
                        The grid recognizes your energy — but the shelves themselves open only for Medusa Room VIP members.
                        Inside: unreleased stems, project files, sample packs, and every early-access drop.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <button onClick={() => subscribe("monthly")} disabled={!!busy} className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FFB800] to-amber-500 text-black font-bold text-xs px-6 py-3 rounded-full hover:shadow-[0_0_24px_rgba(255,184,0,0.55)] active:scale-95 disabled:opacity-60 transition-all" data-testid="vault-join-monthly">
                          {busy === "monthly" ? <Loader2 size={13} className="animate-spin" /> : user ? <Crown size={13} /> : <LogIn size={13} />}
                          {user ? "Start 7-Day Free Trial" : "Sign in & Start Free Trial"}
                        </button>
                        <button onClick={() => subscribe("yearly")} disabled={!!busy} className="inline-flex items-center gap-2 border border-[#FFB800]/50 text-[#FFB800] font-bold text-xs px-6 py-3 rounded-full hover:bg-[#FFB800] hover:text-black active:scale-95 transition-all" data-testid="vault-join-yearly">
                          $99 / Year
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <p className="mt-10 text-center text-xs text-ice/40">
          Want the full studio experience? <Link to="/medusa" className="text-fuchsia-300 hover:text-teal-300 transition-colors" data-testid="vault-to-medusa">Enter The Medusa Room</Link>
        </p>
      </div>
    </main>
  );
}
